import _isNil from 'lodash/isNil';
import { organizationEndPoints } from '@Services/data/ApiHandler';
import store from '@Services/redux/Store';
import { IOrganization } from './Interfaces';
import { OrganizationActions } from './Reducer';

const Api = {
    getOrganizations: async (
        includeEmail?: boolean,
        orgId?: string,
        callback?: () => void
    ) => {
        const response = await organizationEndPoints.getOrganizations(
            includeEmail
        );

        if (response.isSuccess) {
            const organizations: Array<IOrganization> =
                response.data.orgDetails;

            const combined: Array<IOrganization> = [];

            organizations.forEach((org) => {
                const orgRel: any = org.relationship;
                const existingIndex = combined.findIndex(
                    (o) => o.organisation_id === org.organisation_id
                );
                if (existingIndex > -1) {
                    combined[existingIndex].relationship.push(orgRel);
                } else {
                    combined.push({ ...org, relationship: [orgRel] });
                }
            });

            store.dispatch(OrganizationActions.updateOrganizations(combined));

            if (orgId) {
                const foundOrg = combined.find(
                    (o) => o.organisation_id === orgId
                );
                if (!_isNil(foundOrg)) {
                    Api.setSelectedOrganization(foundOrg);
                }
            }

            if (callback) {
                callback();
            }

            return organizations;
        }
    },
    setOrganizations: (organizations: Array<IOrganization>) => {
        store.dispatch(OrganizationActions.updateOrganizations(organizations));
    },
    setSelectedOrganization: (organization: IOrganization) => {
        store.dispatch(
            OrganizationActions.setSelectedOrganization(organization)
        );
    },
    inviteUserToOrganization: async (
        email: string,
        organizationId: string,
        callback?: () => void
    ) => {
        const body = {
            email: email,
            organizationId: organizationId,
        };
        const response = await organizationEndPoints.inviteUserToOrganization(
            body
        );

        if (response.isSuccess) {
            if (callback) {
                callback();
            }
            return response.data;
        }
    },
    _createOrgRequestBody: (
        organizationName: string,
        ownerEmail: string,
        isActive: boolean,
        organizationId?: string
    ): object => {
        const output = {
            organisation_name: organizationName,
            owner_email: ownerEmail,
            is_active: isActive,
        };

        if (organizationId) {
            output['organisation_id'] = organizationId;
        }
        return output;
    },
    createOrganization: async (
        organizationName: string,
        ownerEmail: string,
        isActive: boolean,
        callback?: () => void
    ) => {
        const body = Api._createOrgRequestBody(
            organizationName,
            ownerEmail,
            isActive
        );

        const response = await organizationEndPoints.createOrganization(body);

        if (response.isSuccess) {
            if (callback) {
                callback();
            }
        }
    },
    updateOrganization: async (
        organizationId: string,
        name: string,
        ownerEmail: string,
        isActive: boolean,
        callback?: () => void
    ) => {
        const body = Api._createOrgRequestBody(
            name,
            ownerEmail,
            isActive,
            organizationId
        );

        const response = await organizationEndPoints.editOrganization(body);

        if (response.isSuccess) {
            if (callback) {
                callback();
            }
        }
    },
    getOrganizationMembers: async (
        organizationId: string,
        callback?: () => void
    ) => {
        const response = await organizationEndPoints.getOrganizationUsers(
            organizationId
        );
        if (response.isSuccess) {
            store.dispatch(
                OrganizationActions.updateOrganizationMembers(
                    response.data.userDetails
                )
            );

            if (callback) {
                callback();
            }
        }
    },
    _createOrgMemberRequestBody: async (
        organizationId: string,
        email: string
    ) => {
        const output = {
            organisation_id: organizationId,
            user_email: email,
        };
        return output;
    },
    createOrgMember: async (
        organizationId: string,
        email: string,
        callback?: () => void
    ) => {
        const body = await Api._createOrgMemberRequestBody(
            organizationId,
            email
        );

        const response = await organizationEndPoints.createOrganizationMember(
            body
        );

        if (response.isSuccess) {
            if (callback) {
                callback();
            }
        }
    },
    deleteOrgMember: async (
        organizationId: string,
        email: string,
        callback?: () => void
    ) => {
        const response = await organizationEndPoints.deleteOrganizationMember(
            organizationId,
            email
        );

        if (response.isSuccess) {
            if (callback) {
                callback();
            }
        }
    },
    clearOrgState: () => {
        store.dispatch(OrganizationActions.clearOrganizationState());
    },
};

export default Api;
